// This project has no tsconfig.json, so this reference is needed to pull in
// react-app-env.d.ts's ambient declarations (react-scripts types, *.css
// wildcard module) — an unimported .d.ts otherwise isn't part of this file's
// inferred TS project.
/// <reference path="../../../../../react-app-env.d.ts" />
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useRestaurant } from '../../contexts/RestaurantContext.tsx';
import TableComponent from './TableComponent.tsx';
import ElementComponent from './ElementComponent.tsx';
import { ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';
import '../../styles/floor-plan-canvas.css';

interface FloorPlanCanvasProps {
  onElementSelect: (element: any) => void;
  selectedElement: any;
  isEditing: boolean;
}

const FloorPlanCanvas: React.FC<FloorPlanCanvasProps> = ({
  onElementSelect,
  selectedElement,
  isEditing
}) => {
  const { floors, currentFloor } = useRestaurant();
  const [zoom, setZoom] = useState(0.9);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);

  const canvasRef = useRef<HTMLDivElement>(null);

  const currentFloorData = floors.find(floor => floor.id === currentFloor);
  const canvasSize = currentFloorData
    ? { width: currentFloorData.width, height: currentFloorData.height }
    : { width: 800, height: 600 };

  const handleZoom = useCallback((delta: number) => {
    setZoom(prev => Math.max(0.5, Math.min(2, prev + delta)));
  }, []);

  const handlePanStart = (e: React.MouseEvent) => {
    // Allow panning with middle mouse button or left mouse button on empty areas
    if (e.button === 1 || (e.button === 0 && !(e.target as HTMLElement).closest('.table-component, .element-component'))) {
      setIsDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
    }
  };

  const handlePanMove = (e: React.MouseEvent) => {
    if (!isDragging) return;

    const dx = e.clientX - dragStart.x;
    const dy = e.clientY - dragStart.y;

    setPosition({
      x: position.x + dx,
      y: position.y + dy
    });

    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handlePanEnd = () => {
    setIsDragging(false);
  };

  const handleCanvasClick = (e: React.MouseEvent) => {
    // Only deselect if clicking on empty area and not dragging
    if (!isDragging && !(e.target as HTMLElement).closest('.table-component, .element-component')) {
      onElementSelect(null);
    }
  };

  const resetView = () => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };

  // Listener manual para bloquear scroll da página
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      if (!isHovered) return;
      e.preventDefault();
      e.stopPropagation();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      handleZoom(delta);
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel as EventListener);
  }, [isHovered, handleZoom]);

  // Opcional: impedir que teclas de scroll rolem a página quando mouse está sobre o canvas
  useEffect(() => {
    if (!isHovered) return;
    const onKeyDown = (e: KeyboardEvent) => {
      const keys = [' ', 'ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End'];
      if (keys.includes(e.key)) {
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', onKeyDown, { passive: false });
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isHovered]);

  return (
    <div className="floor-plan-canvas-container">
      <div className="canvas-controls">
        <button onClick={() => handleZoom(0.1)}>
          <ZoomIn size={20} />
        </button>
        <button onClick={() => handleZoom(-0.1)}>
          <ZoomOut size={20} />
        </button>
        <button onClick={resetView}>
          <RotateCcw size={20} />
        </button>
        <div className="zoom-level">{Math.round(zoom * 100)}%</div>
      </div>

      <div
        className="floor-plan-canvas"
        ref={canvasRef}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onMouseDown={handlePanStart}
        onMouseMove={handlePanMove}
        onMouseUp={handlePanEnd}
        onClick={handleCanvasClick}
        style={{
          cursor: isDragging ? 'grabbing' : 'grab'
        }}
      >
        <div
          className="canvas-content"
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
            transformOrigin: '0 0',
            width: `${canvasSize.width}px`,
            height: `${canvasSize.height}px`,
            backgroundImage: currentFloorData
              ? `url(/textures/${currentFloorData.texture}.jpg)`
              : 'none'
          }}
        >
          {currentFloorData && (
            <>
              {currentFloorData.tables.map(table => (
                <TableComponent
                  key={table.id}
                  table={table}
                  isSelected={selectedElement?.id === table.id}
                  isEditing={isEditing}
                  onClick={() => onElementSelect({ ...table, type: 'table' })}
                  scale={zoom}
                />
              ))}

              {currentFloorData.elements.map(element => (
                <ElementComponent
                  key={element.id}
                  element={element}
                  isSelected={selectedElement?.id === element.id}
                  isEditing={isEditing}
                  onClick={() => onElementSelect({ ...element, type: element.type || 'object' })}
                  scale={zoom}
                />
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FloorPlanCanvas;
